import { ImportTarget } from '@app/services/import-sync.service';

/**
 * Import profiles: one definition per import target, shared by the target
 * cards, the column popover, the mapping table, the sample CSV and the file
 * type detection. Required columns mirror api/src/Services/Import/ImportProfiles.php.
 */

export interface ImportProfileColumn {
  /** Header as exported by BMD */
  name: string;
  /** Other accepted spellings (compared after normalizeHeader) */
  aliases?: string[];
  target: string;
  required?: boolean;
  /** Example value for the sample file */
  example?: string;
}

export interface ImportProfile {
  id: ImportTarget;
  title: string;
  icon: string;
  description: string;
  /** Entities created / updated */
  does: string;
  /** What one row stands for */
  rowUnit: string;
  key: string;
  /** Recommended import order (1 = first) */
  order: number;
  examples: string[];
  /** Every group must be present; a group is satisfied by any one of its headers. */
  requiredGroups: string[][];
  columns: ImportProfileColumn[];
}

export const IMPORT_PROFILES: ImportProfile[] = [
  {
    id: 'kunden-master',
    title: 'Kunden-Stammdaten',
    icon: 'groups',
    description: 'BMD-Personenkonten-Export: wer der Kunde ist.',
    does:
      'Legt neue Kunden an, aktualisiert Adresse, Land, E-Mail, Telefon und UID, archiviert Kunden, die nicht mehr ' +
      'in BMD sind, und füllt leere Hausverwaltungsfelder. Bestehende Namen und Hausverwaltungsdaten bleiben unverändert.',
    rowUnit: '1 Zeile = 1 BMD-Kunde (ca. 7.700)',
    key: 'Pers.-Nr. = kunde.alte_kundennummer',
    order: 1,
    examples: ['Kunden-233424_001.xlsx', 'Kunden master.csv'],
    requiredGroups: [['Pers.-Nr.'], ['Nachname', 'Matchcode']],
    columns: [
      { name: 'Pers.-Nr.', aliases: ['Pers-Nr'], target: 'kunde.alte_kundennummer (Schlüssel)', required: true, example: '413751' },
      { name: 'Nachname', target: 'kunde.suchbegriff (nur neue oder leere Namen)', required: true, example: '10, Pernerstorferg. 25' },
      { name: 'Vorname', target: 'kunde.suchbegriff („Nachname, Vorname“)', example: '' },
      { name: 'Matchcode', target: 'kunde.suchbegriff (Fallback ohne Nachname)', example: 'STINGL IMM' },
      { name: 'Straße', aliases: ['Strasse'], target: 'kunde.strasse', example: 'Musterstraße 1' },
      { name: 'PLZ', aliases: ['Plz'], target: 'kunde.plz', example: '1010' },
      { name: 'Ort', target: 'kunde.ort', example: 'Wien' },
      { name: 'Land-Nr.', aliases: ['Land'], target: 'kunde.land (1 = AT, 2 = DE, 17 = CH)', example: '1' },
      { name: 'E-Mail-Adresse', aliases: ['E-Mail'], target: 'kunde.email + Rechnungskontakt', example: 'office@muster.at' },
      { name: 'Telefonnummer', aliases: ['Telefon'], target: 'kunde.telefon + Rechnungskontakt', example: '+43 1 2345678' },
      { name: 'Faxnummer', aliases: ['Fax'], target: 'kunde.fax', example: '' },
      { name: 'UID-Nummer', aliases: ['UID'], target: 'kunde.uid', example: 'ATU12345678' },
      { name: 'Zu Handen', aliases: ['Zu Handen-Info'], target: 'Rechnungskontakt adresse2 / Hausverwaltung zuhanden', example: 'Frau Muster' },
      { name: 'Zusatzname', target: 'Rechnungskontakt unternehmensname / Hausverwaltung', example: 'p.A. Muster Immobilien GmbH' },
      { name: 'Rng-Empf-Nr', aliases: ['Rng Empf Nr'], target: 'Hausverwaltung (Rechnungsempfänger, nur leere Felder)', example: '' },
    ],
  },
  {
    id: 'clients',
    title: 'Verträge / Kundenexport',
    icon: 'description',
    description: 'BMD-Auftragsexport (Rattenviertel rpausch): was je Objekt betreut und verrechnet wird.',
    does:
      'Legt an bzw. aktualisiert Standort (Objekt), Projektaufgabe (Kontrollintervall), Rechnungsvertrag ' +
      '(Preis, Rechnungsintervall), Rechnungskontakt, Hausverwaltung-Zuordnung und Techniker. Kunden werden ' +
      'nur über „Kunde“ zugeordnet, deren Name wird nie überschrieben.',
    rowUnit: '1 Zeile = 1 Vertrag / Objekt (ca. 3.300)',
    key: 'Erf.-Nr. = rechnungsvertrag.erf_nr',
    order: 3,
    examples: ['Test.xlsx', 'Rattenviertel rpausch.csv'],
    requiredGroups: [['Erf.-Nr.'], ['Kunde'], ['VK-Wert netto'], ['Kundenadresse Straße'], ['Kd.-Adresse Zusatzname']],
    columns: [
      { name: 'Erf.-Nr.', target: 'rechnungsvertrag.erf_nr (Schlüssel)', required: true, example: '197842' },
      { name: 'Kunde', target: 'kunde.alte_kundennummer (Zuordnung)', required: true, example: '413751' },
      { name: 'Kunden – Name', aliases: ['Kunden - Name'], target: 'kunde.suchbegriff (nur wenn leer)', example: 'WEG Musterstraße 1' },
      { name: 'Objekt Straße', target: 'standort.strasse + standort.name', example: 'Musterstraße 1' },
      { name: 'Objekt PLZ', target: 'standort.plz + standort.ort', example: '1010 Wien' },
      { name: 'Kopftext (Plaintext)', target: 'standort.kopftext_raw → Zugang/Kontakt/Boxen/Notiz', example: '' },
      { name: 'Notiz', target: 'standort.objekt_notiz (Fallback)', example: '' },
      { name: 'Vertreter 1 - Name', target: 'standort.techniker_id (Techniker zuordnen/anlegen)', example: 'Max Muster' },
      { name: 'Vertr.-ID 1', target: 'mitarbeiter.bmd_vertreter_id', example: '12' },
      { name: 'Auftragsscheinintervall', target: 'projektaufgabe.periodizitaet', example: 'vierteljährlich' },
      { name: 'Druckmonat Auftragsschein', target: 'projektaufgabe.periodizitat_serie', example: '1' },
      { name: 'Freifeld 25', target: 'standort.soll_viertel', example: '' },
      { name: 'Ihr Zeichen', target: 'standort.auftragsbestaetigung_erforderlich', example: '' },
      { name: 'Rg.-Intervall', target: 'rechnungsvertrag.rg_intervall_type', example: 'jährlich' },
      { name: 'VK-Wert netto', aliases: ['VK Wert Netto', 'VK-Wert'], target: 'rechnungsvertrag.vk_wert_netto', required: true, example: '97,77' },
      { name: 'VK-Wert brutto', aliases: ['VK Wert Brutto'], target: 'rechnungsvertrag.vk_wert_brutto', example: '117,32' },
      { name: 'Verr.KZ', target: 'rechnungsvertrag.nicht_verrechnen (von Fakturierung ausgenommen)', example: '' },
      { name: 'Bestelldat.', target: 'rechnungsvertrag.start_datum / kunde.startdatum', example: '01.01.2024' },
      { name: 'Erf.-Datum', target: 'rechnungsvertrag.erf_datum', example: '26.10.2022' },
      { name: 'Kd.-Bestell-Nr.', aliases: ['Kd-Bestellnr'], target: 'rechnungsvertrag.kd_bestellnr', example: '' },
      { name: 'Zlg-Ziel', target: 'rechnungsvertrag.zahlungsziel_tage', example: '14' },
      { name: 'Kundenadresse Straße', aliases: ['Kd-Adresse Straße', 'Kd.-Adresse Straße'], target: 'Rechnungskontakt adresse', required: true, example: 'Musterstraße 1' },
      { name: 'Kd.-Adresse zu Handen Info', aliases: ['Kd-Adresse zu Handen-Info'], target: 'Rechnungskontakt adresse2', example: '' },
      { name: 'Kd.-Adresse Zusatzname', aliases: ['Kd-Adresse Zusatzname'], target: 'Rechnungskontakt unternehmensname / Hausverwaltung', required: true, example: '' },
    ],
  },
  {
    id: 'artikel',
    title: 'Artikel / Produkte / Services',
    icon: 'inventory_2',
    description: 'BMD-Artikelexport: Services, Pauschalen und Produkte.',
    does:
      'Legt Artikel an bzw. aktualisiert Bezeichnung, Preis, Einheit und Inaktiv-Kennzeichen. ' +
      'r* = Service/Pauschale, s* = Produkt (oder Typ manuell wählen).',
    rowUnit: '1 Zeile = 1 Artikel',
    key: 'Art.-Nr. = artikel.nummer',
    order: 2,
    examples: ['Artikel.xlsx', 'Artikelexport.csv'],
    requiredGroups: [['Art.-Nr.'], ['Art.-Bez.']],
    columns: [
      { name: 'Art.-Nr.', target: 'artikel.nummer (Schlüssel)', required: true, example: 'r100' },
      { name: 'Art.-Bez.', target: 'artikel.beschreibung', required: true, example: 'Service-Pauschale' },
      { name: 'Notiz', target: 'artikel.belegtext', example: '' },
      { name: 'Grund EH', target: 'artikel.grund_eh / basiseinheit', example: 'Stk' },
      { name: 'Kto-Tab', target: 'artikel.kto_tab', example: '' },
      { name: 'VK exkl. USt', target: 'artikel.vk_exkl_ust', example: '97,77' },
      { name: 'EK-Preis', target: 'artikel.ek_preis', example: '' },
      { name: 'EP', target: 'artikel.ep', example: '' },
      { name: 'Indexerh.', target: 'artikel.indexerhoehung', example: '' },
      { name: 'Hauptlieferant', target: 'artikel.hauptlieferant', example: '' },
      { name: 'Lagermenge', target: 'artikel.lagermenge', example: '' },
      { name: 'Inaktiv', target: 'artikel.inaktiv', example: '' },
    ],
  },
];

export function importProfile(id: ImportTarget): ImportProfile {
  return IMPORT_PROFILES.find((p) => p.id === id) as ImportProfile;
}

/** Same rules as CsvReader::normalizeHeader on the API. */
export function normalizeHeader(header: string): string {
  return String(header ?? '')
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\./g, '')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface ProfileMatch {
  profile: ImportProfile;
  missingRequired: string[];
  /** Profile columns found in the file */
  found: string[];
  /** Profile columns not found in the file */
  missing: string[];
  /** Profile column → header as spelled in the file */
  headerFor: { [column: string]: string };
  /** found / profile columns, 0..1 */
  coverage: number;
}

export interface ProfileDetection {
  /** Best fitting profile with all required columns, or null */
  best: ProfileMatch | null;
  matches: ProfileMatch[];
  /** File headers not used by the selected profile */
  unusedHeaders: string[];
}

export function matchProfile(profile: ImportProfile, headers: string[]): ProfileMatch {
  const byNormalized = new Map<string, string>();
  headers.forEach((h) => {
    const n = normalizeHeader(h);
    if (n !== '' && !byNormalized.has(n)) {
      byNormalized.set(n, h);
    }
  });
  const fileHeader = (name: string, aliases: string[] = []) =>
    [name, ...aliases].map((h) => byNormalized.get(normalizeHeader(h))).find((h) => h !== undefined);

  const missingRequired = profile.requiredGroups
    .filter((group) => !group.some((h) => fileHeader(h, profile.columns.find((c) => c.name === h)?.aliases) !== undefined))
    .map((group) => group.join(' oder '));
  const headerFor: { [column: string]: string } = {};
  profile.columns.forEach((c) => {
    const h = fileHeader(c.name, c.aliases);
    if (h !== undefined) {
      headerFor[c.name] = h;
    }
  });
  const found = Object.keys(headerFor);
  const missing = profile.columns.filter((c) => !(c.name in headerFor)).map((c) => c.name);

  return {
    profile,
    missingRequired,
    found,
    missing,
    headerFor,
    coverage: profile.columns.length ? found.length / profile.columns.length : 0,
  };
}

export function detectImportProfile(headers: string[], selected: ImportTarget): ProfileDetection {
  const matches = IMPORT_PROFILES.map((p) => matchProfile(p, headers)).sort((a, b) => b.coverage - a.coverage);
  const best = matches.find((m) => m.missingRequired.length === 0) || null;

  const selectedProfile = importProfile(selected);
  const known = new Set(selectedProfile.columns.flatMap((c) => [c.name, ...(c.aliases || [])]).map(normalizeHeader));
  const unusedHeaders = headers.filter((h) => h.trim() !== '' && !known.has(normalizeHeader(h)));

  return { best, matches, unusedHeaders };
}

/** Semicolon CSV with the profile headers and one example row (UTF-8 with BOM for Excel). */
export function sampleCsv(profile: ImportProfile): string {
  const escape = (v: string) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const header = profile.columns.map((c) => escape(c.name)).join(';');
  const row = profile.columns.map((c) => escape(c.example ?? '')).join(';');
  return '\uFEFF' + header + '\r\n' + row + '\r\n';
}
